/**
 * The model — the book, repriced off the golf that has actually happened.
 *
 * The /book page froze the field's chances the night the lines were posted.
 * Scott's ask: keep that analysis ALIVE as the week runs, and let the group
 * watch it evolve day by day without anything ever disappearing. So this
 * screen runs the Monte Carlo from `engine/model` right on the phone — a
 * couple of thousand simulated weeks through the real scoring engine — and
 * the day chips are snapshots DERIVED from the stored scores: "Monday night"
 * is recomputed, identically, every time anyone asks for it. Nothing is
 * saved; nothing can go stale; nothing goes away.
 *
 * Fair prices, no vig. What the bookie quotes on the board is his business.
 */

import { useEffect, useRef, useState } from 'react'
import { POKES_2026 } from '../data/pokes-2026'
import { pokeFor } from '../engine/pokes'
import { holesFor } from '../engine/handicap'
import { formatAmerican, impliedProbability } from '../engine/odds'
import {
  priceTheField,
  scoresThrough,
  fairAmerican,
  type ModelRow,
} from '../engine/model'
import type { Tournament } from '../engine/types'
import { computeAll, inPlayOrder, playerName, todayLocal, weekdayOf } from '../select'
import {
  mergedConfirmations,
  mergedScores,
  postOdds,
  updateOdds,
  useStore,
  type Book,
} from '../store'

const SIMS = 1500
const CHUNKS = 10

interface Snap {
  rows: ModelRow[]
  sims: number
  knownScores: number
  /** Which golf the dice were conditioned on, round by round. */
  inBook: { label: string; have: number; of: number }[]
}

/**
 * The opening chances, from the posted board with the margin taken out.
 *
 * The first price in each line's history is the true open; the board today
 * may have moved. Normalized so the field sums to one — a book's raw implied
 * numbers sum to more than that, and the surplus is the bookie's, not golf's.
 */
function openingRows(book: Book | null): ModelRow[] | null {
  if (!book) return null
  const open = book.odds.filter(
    (o) => o.market === 'outright' && o.subject_id && o.status === 'open',
  )
  if (open.length === 0) return null
  const first = new Map<string, number>()
  for (const h of book.history ?? []) if (!first.has(h.odds_id)) first.set(h.odds_id, h.american_odds)
  const raw = open.map((o) => ({
    playerId: o.subject_id!,
    posted: first.get(o.id) ?? o.american_odds,
  }))
  const total = raw.reduce((s, r) => s + impliedProbability(r.posted), 0) || 1
  // The chance is de-vigged; the PRICE is the one that was actually posted.
  return raw
    .map((r) => ({
      playerId: r.playerId,
      p: impliedProbability(r.posted) / total,
      fair: r.posted,
    }))
    .sort((a, b) => b.p - a.p)
}

/**
 * Where the dial starts: a hair BELOW fair.
 *
 * It used to open on the board's own overround, which on this book is about
 * 34% — and Scott's ruling is that the week's betting leaned much too hard
 * to the house. So the default is now 0.95: every quote pays about five
 * percent more than the true chance is worth, the bookie keeps nothing, and
 * he has to reach for the stepper to take anything at all. The stepper
 * still goes up for the day he wants a margin back.
 */
const DEFAULT_MARGIN = 0.95

/**
 * A model price the way a board would print it: quarters above two-to-one,
 * tens below. +743 is a simulation artifact; +750 is a line.
 */
function roundQuote(a: number): number {
  const step = Math.abs(a) >= 200 ? 25 : 10
  const q = Math.round(a / step) * step
  // American odds have no numbers between -99 and +99.
  return Math.abs(q) < 100 ? (q >= 0 ? 100 : -100) : q
}

export function Model({
  doc,
  book,
  mayBook,
  onPosted,
}: {
  doc: Tournament
  book: Book | null
  /** The bookie and the admins: the margin control and the load button. */
  mayBook: boolean
  /** Refetches the board after the model's prices are loaded onto it. */
  onPosted: () => Promise<void> | void
}) {
  const scores = useStore(mergedScores)
  const today = todayLocal()

  /*
   * The chips: the open, each day that has golf in the book, and now. A date
   * only earns a chip once it holds a score — before the trip there is
   * nothing between the posted lines and the model's own flat read.
   */
  const dates = [...new Set(inPlayOrder(doc.rounds).map((r) => r.date))].filter(
    (d): d is string => Boolean(d) && d < today,
  )
  const dayChips = dates.filter((d) => scoresThrough(doc, scores, d).length > 0)
  /*
   * The latest day holding any golf — the anchor for "Now"'s dice. Scott's
   * question, and the right one: Now and a day chip describing the SAME
   * scores must show the SAME numbers, so they must roll the same dice.
   * Seeding Now off the anchor date (not the literal word "now") makes
   * Monday morning's Now bit-for-bit equal to the Sunday chip it just
   * finished being — and a chip for today never renders while Now covers
   * it, so two views of one book can never disagree by noise.
   */
  const scoredDates = new Set(
    scores.map((sc) => doc.rounds.find((r) => r.id === sc.roundId)?.date).filter(Boolean),
  )
  const anchor = [...scoredDates].sort().pop() ?? null
  const seedBaseFor = (key: string) =>
    key === 'now' ? (anchor ? `d:${anchor}` : 'open') : `d:${key}`

  const [chip, setChip] = useState<string>('now')
  const [snaps, setSnaps] = useState<Record<string, Snap>>({})
  const [running, setRunning] = useState<{ key: string; done: number } | null>(null)
  const busy = useRef(false)

  const opening = openingRows(book)
  const openingP = new Map((opening ?? []).map((r) => [r.playerId, r.p]))
  /*
   * The margin starts where the live board is and is the bookie's to shift —
   * a display dial until the load button turns it into actual lines.
   */
  const [marginPct, setMarginPct] = useState<number | null>(null)
  const margin = marginPct != null ? 1 + marginPct / 100 : DEFAULT_MARGIN
  const [loading, setLoading] = useState<'armed' | 'posting' | 'done' | null>(null)
  /*
   * Edit mode, for Josh and the admins: the margin dial, the per-man price
   * sliders and the load button all live behind it, so the screen everybody
   * else reads stays a board and not a cockpit.
   */
  const [editing, setEditing] = useState(false)
  /*
   * The bookie's disagreements — implied chance per man, local to this phone
   * and this edit session. Never synced, never saved: they exist only in the
   * gap between "the model says" and "the house clicks Load".
   */
  const [tweaks, setTweaks] = useState<Record<string, number>>({})
  /** The quote for a man: his tweak if the bookie moved him, else the model at margin. */
  const quoteQ = (pid: string, p: number) => tweaks[pid] ?? Math.min(0.97, p * margin)

  const confirmations = useStore(mergedConfirmations)
  const settled = computeAll(doc, scores, confirmations).standings.rows
  const pointsOf = new Map(settled.map((r) => [r.playerId, r.points]))

  /*
   * The whole point of the dial: one tap turns the current snapshot into the
   * live board. Existing lines move (each move lands in odds_history, so the
   * charts record it); missing lines are posted fresh. The HOUSE is skipped —
   * the bookie carries no line on himself, and the server would refuse it
   * anyway. Armed-then-fire, because this rewrites eleven prices at once.
   */
  const loadOntoBoard = async () => {
    const snap = snaps[chip]
    if (!snap || loading === 'posting') return
    if (loading !== 'armed') {
      setLoading('armed')
      return
    }
    setLoading('posting')
    try {
      const openLines = new Map(
        (book?.odds ?? [])
          .filter((o) => o.market === 'outright' && o.subject_id && o.status !== 'settled')
          .map((o) => [o.subject_id!, o]),
      )
      for (const r of snap.rows) {
        if (r.playerId === doc.settings.bookie) continue
        const quote = roundQuote(fairAmerican(quoteQ(r.playerId, r.p)))
        const line = openLines.get(r.playerId)
        if (line && line.american_odds === quote) continue
        if (line) await updateOdds(line.id, { americanOdds: quote })
        else
          await postOdds({
            market: 'outright',
            subjectId: r.playerId,
            description: `${playerName(doc, r.playerId)} to win the Woodsmen`,
            americanOdds: quote,
          })
      }
      await onPosted()
      setLoading('done')
      window.setTimeout(() => setLoading(null), 2500)
    } catch {
      setLoading(null)
    }
  }

  /*
   * Chunked so the thumb stays alive: ten slices of the same deterministic
   * run (the seed carries the slice index), UI breathing between them. The
   * result for a key is identical every time it is computed — see the module
   * note — so finishing a run is as good as having stored it.
   */
  const run = async (key: string) => {
    if (busy.current || snaps[key]) return
    busy.current = true
    setRunning({ key, done: 0 })
    const known = scoresThrough(doc, scores, key === 'now' ? null : key)
    /*
     * The receipts: which golf the dice were conditioned on, so "off 62
     * scores" is checkable instead of taken on faith — Scott's ask after the
     * count alone told him nothing.
     */
    const byRound = new Map<string, number>()
    for (const sc of known) byRound.set(sc.roundId, (byRound.get(sc.roundId) ?? 0) + 1)
    const inBook = inPlayOrder(doc.rounds)
      .filter((r) => byRound.has(r.id))
      .map((r) => {
        const course = doc.courses.find((cc) => cc.id === r.courseId)
        const holes = course ? holesFor(course, r.nine).length : 0
        const seats = doc.groups
          .filter((g) => g.roundId === r.id)
          .reduce((n, g) => n + g.playerIds.length, 0)
        return { label: r.label, have: byRound.get(r.id)!, of: holes * seats }
      })
    const wins = new Map<string, number>()
    let done = 0
    const seedBase = seedBaseFor(key)
    for (let c = 0; c < CHUNKS; c++) {
      const part = priceTheField(doc, known, { sims: SIMS / CHUNKS, seedKey: `${seedBase}#${c}` })
      for (const r of part.rows) wins.set(r.playerId, (wins.get(r.playerId) ?? 0) + r.p * part.sims)
      done += part.sims
      setRunning({ key, done })
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
    const rows = doc.players
      .filter((p) => p.active)
      .map((p) => {
        const prob = (wins.get(p.id) ?? 0) / done
        return { playerId: p.id, p: prob, fair: fairAmerican(prob) }
      })
      .sort((a, b) => b.p - a.p)
    setSnaps((s) => ({ ...s, [key]: { rows, sims: done, knownScores: known.length, inBook } }))
    setRunning(null)
    busy.current = false
  }

  // The first thing anyone wants is now; start on it without being asked.
  useEffect(() => {
    void run('now')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shown: { rows: ModelRow[]; caption: string } | null =
    chip === 'opening'
      ? opening
        ? { rows: opening, caption: 'The posted board, margin removed — where the week started.' }
        : null
      : snaps[chip]
        ? {
            rows: snaps[chip]!.rows,
            caption:
              chip === 'now'
                ? `${snaps[chip]!.sims.toLocaleString()} simulated weeks off ${snaps[chip]!.knownScores} scores in the book.`
                : `As of ${weekdayOf(chip)} night — ${snaps[chip]!.sims.toLocaleString()} simulated weeks off the ${snaps[chip]!.knownScores} scores in by then. This snapshot never changes.`,
          }
        : null

  const maxP = Math.max(0.01, ...(shown?.rows.map((r) => r.p) ?? [0.01]))

  return (
    <div>
      <div className="flex gap-2 px-4 py-2.5 overflow-x-auto border-b border-rule">
        {opening && (
          <button
            type="button"
            onClick={() => setChip('opening')}
            aria-pressed={chip === 'opening'}
            className={`bc-tap bc-label px-3 shrink-0 border ${
              chip === 'opening' ? 'bg-ink text-paper border-ink' : 'border-rule bg-paper'
            }`}
          >
            Opening
          </button>
        )}
        {dayChips.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => {
              setChip(d)
              void run(d)
            }}
            aria-pressed={chip === d}
            className={`bc-tap bc-label px-3 shrink-0 border ${
              chip === d ? 'bg-ink text-paper border-ink' : 'border-rule bg-paper'
            }`}
          >
            {weekdayOf(d)}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setChip('now')
            void run('now')
          }}
          aria-pressed={chip === 'now'}
          className={`bc-tap bc-label px-3 shrink-0 border ${
            chip === 'now' ? 'bg-signal text-paper border-signal' : 'border-rule bg-paper'
          }`}
        >
          Now
        </button>
        {mayBook && (
          <button
            type="button"
            aria-pressed={editing}
            onClick={() => {
              setEditing((v) => {
                // Leaving edit mode abandons the disagreements, deliberately:
                // a tweak either became the board or it never happened.
                if (v) {
                  setTweaks({})
                  setLoading(null)
                }
                return !v
              })
            }}
            className={`bc-tap bc-label px-3 shrink-0 border ml-auto ${
              editing ? 'bg-ink text-paper border-ink' : 'border-ink bg-paper'
            }`}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>

      {mayBook && editing && chip !== 'opening' && (
        <div className="px-4 py-2 border-b border-rule flex items-center gap-2 flex-wrap">
          <span className="bc-label text-mute shrink-0">Margin</span>
          <button
            type="button"
            onClick={() => setMarginPct(Math.max(-25, Math.round((margin - 1) * 100) - 5))}
            className="bc-tap px-3 border border-rule bc-num"
            aria-label="Less margin"
          >
            −
          </button>
          <span className="bc-num text-sm w-12 text-center">
            {Math.round((margin - 1) * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setMarginPct(Math.min(100, Math.round((margin - 1) * 100) + 5))}
            className="bc-tap px-3 border border-rule bc-num"
            aria-label="More margin"
          >
            +
          </button>
          <button
            type="button"
            disabled={!snaps[chip] || loading === 'posting'}
            onClick={() => void loadOntoBoard()}
            className={`bc-tap bc-label px-3 ml-auto disabled:opacity-40 ${
              loading === 'armed' ? 'bg-signal text-paper' : 'bg-ink text-paper'
            }`}
          >
            {loading === 'posting'
              ? 'Posting…'
              : loading === 'armed'
                ? 'Tap again to post'
                : loading === 'done'
                  ? 'Board is live'
                  : 'Load'}
          </button>
        </div>
      )}

      {running && running.key === chip && (
        <p className="px-4 py-3 text-[13px] text-mute">
          Playing the week out {Math.round((running.done / SIMS) * 100)}% —{' '}
          {running.done.toLocaleString()} of {SIMS.toLocaleString()} simulated weeks…
        </p>
      )}

      {shown && (
        <>
          <ul className="px-4 py-2">
            {shown.rows.map((r) => {
              const base = openingP.get(r.playerId)
              const delta = chip !== 'opening' && base != null ? (r.p - base) * 100 : null
              return (
                <li key={r.playerId} className="py-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="bc-name text-sm min-w-0 truncate">
                      {playerName(doc, r.playerId)}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="bc-num text-lg">{(r.p * 100).toFixed(1)}%</span>
                      <span className="bc-num text-[15px] text-mute">
                        {' '}·{' '}
                        {formatAmerican(
                          chip === 'opening'
                            ? r.fair
                            : roundQuote(fairAmerican(quoteQ(r.playerId, r.p))),
                        )}
                      </span>
                      {delta != null && Math.abs(delta) >= 0.5 && (
                        <span
                          className={`text-[12px] pl-1.5 ${delta > 0 ? 'text-signal' : 'text-mute'}`}
                        >
                          {delta > 0 ? '▲' : '▼'}{Math.abs(delta).toFixed(1)}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* One series, one hue; the labels carry the numbers. */}
                  <div className="h-1.5 mt-1 bg-wash">
                    <div
                      className="h-full bg-signal"
                      style={{ width: `${(r.p / maxP) * 100}%` }}
                    />
                  </div>
                  {editing && chip !== 'opening' && r.playerId !== doc.settings.bookie && (
                    <div className="mt-1 flex items-center gap-2">
                      {/*
                        The dial: slides the QUOTE's implied chance. Local to
                        this phone; it only becomes real through Load to
                        board. Range inputs get exact values, unlike iOS time
                        steps — see the massage picker's scar.
                      */}
                      <input
                        type="range"
                        min={2}
                        max={50}
                        step={0.5}
                        value={quoteQ(r.playerId, r.p) * 100}
                        onChange={(e) =>
                          setTweaks((t) => ({
                            ...t,
                            [r.playerId]: Number(e.target.value) / 100,
                          }))
                        }
                        aria-label={`Quote for ${playerName(doc, r.playerId)}`}
                        className="bc-tap flex-1 accent-current text-signal"
                      />
                      {tweaks[r.playerId] != null && (
                        <button
                          type="button"
                          onClick={() =>
                            setTweaks((t) => {
                              const next = { ...t }
                              delete next[r.playerId]
                              return next
                            })
                          }
                          className="bc-tap bc-label px-2 border border-rule shrink-0"
                        >
                          model
                        </button>
                      )}
                    </div>
                  )}
                  {(() => {
                    /*
                     * The pokes: the opening sheet's hand-written lines on the
                     * Opening chip; every later snapshot writes its own from
                     * what it can see — movement, banked points, position —
                     * picked by a hash of (snapshot, man) so every phone
                     * tells the same joke and yesterday's never changes.
                     */
                    const text =
                      chip === 'opening'
                        ? POKES_2026[r.playerId]
                        : pokeFor(seedBaseFor(chip), {
                            name: playerName(doc, r.playerId),
                            p: r.p,
                            openingP: openingP.get(r.playerId) ?? null,
                            position: shown.rows.findIndex((x) => x.playerId === r.playerId) + 1,
                            fieldSize: shown.rows.length,
                            points: pointsOf.get(r.playerId) ?? 0,
                            isHouse: r.playerId === doc.settings.bookie,
                          })
                    return text ? (
                      <p className="text-[12px] text-mute leading-snug mt-1">{text}</p>
                    ) : null
                  })()}
                </li>
              )
            })}
          </ul>
          <p className="px-4 pb-2 text-[12px] text-mute leading-snug">{shown.caption}</p>
          {chip !== 'opening' && snaps[chip] && (
            <p className="px-4 pb-2 text-[12px] text-mute leading-snug">
              {snaps[chip]!.inBook.length === 0
                ? 'Nothing in the book yet — every hole below is dice.'
                : `In the book: ${snaps[chip]!.inBook
                    .map((r) => `${r.label} ${r.have}/${r.of}`)
                    .join(' · ')}. Everything else is dice.`}
            </p>
          )}
        </>
      )}

      <p className="px-4 pb-4 text-[12px] text-mute leading-snug">
        The percentages are the honest chances. The prices beside them carry{' '}
        {margin < 1
          ? `${Math.round((1 - margin) * 100)}% in the player's favor`
          : margin > 1
            ? `a ${Math.round((margin - 1) * 100)}% house margin`
            : 'no margin either way'}
        , so the bookie can lift them straight onto the board. Arrows are movement
        against the opening lines.
        A flat field before any golf is what a properly handicapped week looks like; the
        golf is what moves it. Every game is played by the app&rsquo;s own scoring engine,
        flights seeded off each simulated week the way the real one will be.
      </p>
    </div>
  )
}
